// Guide operative in-app (pagina /guides, 2026-09-18). Ogni guida è un file Markdown in questa
// cartella, importato come testo (Vite `?raw`) e renderizzato da components/Markdown.tsx: per
// aggiungerne una basta un nuovo .md e una riga qui sotto, nessun altro codice.
//
// Segnaposto `{{chiave}}` nel testo: risolti a runtime da pages/GuidesPage.tsx con il contesto
// letto da GET /api/guides/context (host/porta del gateway da Platform Settings e, se la guida
// è aperta da Sites & Brokers con ?brokerId=, nome di broker/sede/cliente e nome esatto del
// pacchetto scaricato). Senza contesto valgono i `defaults` — scelti in modo che i comandi
// restino comunque incollabili (es. il glob `soc-broker-*.tar.gz` al posto del nome esatto).
import brokerInstall from './broker-install.md?raw';
import brokerInstallImage from './broker-install-image.md?raw';
import windowsLogs from './windows-logs.md?raw';
import athenaPush from './athena-push.md?raw';

export interface Guide {
  slug: string;
  title: string;
  summary: string;
  audience: string;
  minutes: string;
  // Guida visibile solo ai platform admin (integrazione sospesa o riservata).
  platformAdminOnly?: boolean;
  // Chiave del contesto che personalizza la guida (mostrata nel banner in testa alla pagina).
  contextOf?: 'broker';
  // Guide alternative per lo stesso compito (stesso contextOf): etichetta del selettore in testa.
  variant?: string;
  // PDF statico in frontend/public/guides/, con gli esempi al posto dei valori reali.
  pdf?: string;
  content: string;
  defaults: Record<string, string>;
}

export const GUIDES: Guide[] = [
  {
    slug: 'broker-install',
    title: 'Installare il broker su una VM esistente',
    summary: 'La VM Linux è del cliente: installare Docker, creare sede e broker in console, installare il pacchetto e collegare gli apparati.',
    audience: 'Tecnico IT in sede',
    minutes: '30–45 min',
    contextOf: 'broker',
    variant: 'VM esistente del cliente',
    pdf: '/guides/guida-broker-vm-esistente.pdf',
    content: brokerInstall,
    defaults: { package_file: 'soc-broker-*.tar.gz', broker_name: '<nome broker>', tenant_name: '<cliente>', site_name: '<sede>' },
  },
  {
    slug: 'broker-install-image',
    title: "Installare il broker dall'immagine NextGen",
    summary: "La VM nasce dall'immagine pronta (Sangfor HCI, VMware, KVM): importarla, configurarla con broker-firstboot, poi pacchetto dalla console e installazione.",
    audience: 'Tecnico IT in sede',
    minutes: '30–45 min',
    contextOf: 'broker',
    variant: 'Immagine NextGen',
    pdf: '/guides/guida-broker-immagine-nextgen.pdf',
    content: brokerInstallImage,
    defaults: { package_file: 'soc-broker-*.tar.gz', broker_name: '<nome broker>', tenant_name: '<cliente>', site_name: '<sede>' },
  },
  {
    slug: 'windows-logs',
    title: 'Inviare i log Windows / Active Directory al broker',
    summary: 'Audit policy, installazione e configurazione di NXLog CE, test e cosa fanno scattare le regole Windows.',
    audience: 'Tecnico IT / sistemista Windows',
    minutes: '30 min per server, più la GPO',
    content: windowsLogs,
    defaults: {},
  },
  {
    slug: 'athena-push',
    platformAdminOnly: true,
    title: 'Collegare Sangfor Athena XDR in push',
    summary: 'Allowlist degli IP di Athena, mapping dei tenant, configurazione "Third-party platform" e verifica dei log in arrivo.',
    audience: 'Platform admin / analista SOC',
    minutes: '20 min',
    content: athenaPush,
    defaults: { athena_push_port: '6515' },
  },
];

export function findGuide(slug: string | undefined): Guide | undefined {
  return GUIDES.find(g => g.slug === slug);
}

export function fillPlaceholders(text: string, values: Record<string, string>): string {
  return text.replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (m, key: string) => values[key] ?? m);
}

// Guide visibili all'utente corrente (le riservate solo ai platform admin).
export function visibleGuides(user: { scopeLevel?: string; role?: string } | null | undefined): Guide[] {
  const platformAdmin = user?.scopeLevel === 'platform' && user?.role === 'admin';
  return GUIDES.filter(g => !g.platformAdminOnly || platformAdmin);
}
